import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { ExternalLink, Maximize2, Minimize2, RefreshCw } from "lucide-react";

const EmbeddedExternalPage = ({ title, url }) => {
  const containerRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await containerRef.current?.requestFullscreen();
      }
    } catch (error) {
      // Fullscreen can be denied by the browser or operating system.
      console.error("Unable to change fullscreen mode", error);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: isFullscreen
          ? "100vh"
          : { xs: "calc(100vh - 104px)", md: "calc(100vh - 120px)" },
        minHeight: 520,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#fff",
        border: "1px solid #e5eaf2",
        borderRadius: isFullscreen ? 0 : 2.5,
        boxShadow: "0 6px 18px rgba(20, 36, 66, 0.055)",
      }}
    >
      <Box
        sx={{
          minHeight: 58,
          px: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid #e5eaf2",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            component="h1"
            sx={{ color: "#172033", fontSize: 17, fontWeight: 800 }}
          >
            {title}
          </Typography>
          <Typography
            noWrap
            sx={{ color: "#6d7b91", fontSize: 12, maxWidth: 640 }}
          >
            {url}
          </Typography>
        </Box>

        <Tooltip title="Refresh page">
          <IconButton
            aria-label={`Refresh ${title}`}
            onClick={() => setIframeKey((key) => key + 1)}
          >
            <RefreshCw size={20} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${title} in a new tab`}
          >
            <ExternalLink size={20} />
          </IconButton>
        </Tooltip>
        <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
          <IconButton
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        key={iframeKey}
        component="iframe"
        src={url}
        title={title}
        allow="fullscreen"
        allowFullScreen
        sx={{ flexGrow: 1, width: "100%", border: 0, bgcolor: "#fff" }}
      />
    </Box>
  );
};

export default EmbeddedExternalPage;
